import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  getBcAccount,
  saveBcAccount,
  getBcAccountPlayers,
  getBcIngestProfiles,
  getPlugins,
  updateBCPlayer,
  uploadPluginAssets,
} from "../utils/adminFunctions";

import Spinner from "../Spinner";
import Input from "../Input";
import Button from "../Button";
import Modal from "../Modal";
import { addNotification } from "../Notification";
import { LinkIcon } from "../utils/svgs";
import { t } from "../../i18n/language";

const pluginAssetUrls = (plugin) => [
  ...(plugin?.scripts || []),
  ...(plugin?.stylesheets || []),
];

/** Pull the installed plugin version out of a player's script URLs, e.g.
 *  ".../equalplay_1.1.38.js" -> "1.1.38". Returns null when it can't tell —
 *  files uploaded by hand through Studio get an opaque generated name. */
const installedPluginVersion = (plugin) => {
  for (const url of pluginAssetUrls(plugin)) {
    const match = /equalplay[_-]v?(\d+\.\d+\.\d+)/i.exec(url);
    if (match) return match[1];
  }
  return null;
};

// Brightcove-hosted assets turn up under any of three schemes: Studio writes
// http://, our upload pins https://, and older configs use protocol-relative
// //players.brightcove.net. All three are fine — only a non-Brightcove host
// is a problem.
const BC_HOSTED = /^(https?:)?\/\/players\.brightcove\.net\//i;

/** True when any asset is served from somewhere other than Brightcove. That is
 *  exactly what trips "Inactive — Outdated Asset URL(s)" on the player. */
const hasExternalAssets = (plugin) => {
  const urls = pluginAssetUrls(plugin);
  if (urls.length === 0) return false;
  return urls.some((url) => !BC_HOSTED.test(url));
};

function BcAccount() {
  const [account, setAccount] = useState();
  const [name, setName] = useState();
  const [playerId, setPlayerId] = useState();
  const [signLanguageProfile, setSignLanguageProfile] = useState("");
  // Seeds the label on every new sign language track this account creates.
  // "SL" is generic; the abbreviation is national (BSL, ISL, DGS, STS...).
  const [signLanguageLabel, setSignLanguageLabel] = useState("");
  const [ingestProfiles, setIngestProfiles] = useState();
  const [ingestProfilesUnauthorized, setIngestProfilesUnauthorized] =
    useState(false);
  const { bcid } = useParams();
  const [players, setPlayers] = useState();
  const [updateModal, setUpdateModal] = useState();
  const [updating, setUpdating] = useState(false);
  // Every plugin build in the `plugins` collection, newest first.
  const [pluginVersions, setPluginVersions] = useState();
  // `ver` of the build the operator has chosen to install.
  const [selectedPluginVer, setSelectedPluginVer] = useState("");

  const handleSave = () => {
    saveBcAccount({
      account_id: account.account_id || bcid,
      name,
      player_id: playerId,
      signLanguageProfile,
      signLanguageLabel: signLanguageLabel.trim(),
    })
      .then(() => {
        setAccount(null);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getPlayersWithPlugin = () => {
    if (!players) return [];
    const playersWithPlugin = [];
    players.forEach((player) => {
      const plugins = player.branches.master.configuration.plugins;
      let plugin = plugins && plugins.find((plug) => plug.name === "equalplay");
      if (plugin) {
        playersWithPlugin.push({ name: player.name, id: player.id, plugin });
        // console.log(player.name, player.id, plugin);
      }
    });
    return playersWithPlugin;
  };

  const getPlayers = (account) => {
    getBcAccountPlayers(account)
      .then((response) => {
        if (response.currentTokenExpires !== account.currentTokenExpires) {
          console.info("new token as well");
          let oldAccount = { ...account };
          oldAccount.currentTokenExpires = response.currentTokenExpires;
          oldAccount.currentToken = response.currentToken;
          setAccount(oldAccount);
        }
        setPlayers(response.players.items);
        console.log("Got players");
      })
      .catch((err) => {
        console.error("Error getting players", err);
      });
  };

  const filterPlayer = (id) => {
    if (!players || !id) {
      return null;
    }
    const player = players.find((p) => p.id === id);
    return player;
  };

  const updatePlayer = async (id) => {
    const player = filterPlayer(id);
    const chosen = (pluginVersions || []).find(
      (p) => p.ver === selectedPluginVer
    );

    if (!player || !chosen) {
      throw new Error("Missing details for player update");
    }

    if (!chosen.js && !chosen.css) {
      throw new Error(`Plugin ${chosen.ver} has no JS or CSS URL`);
    }

    // Brightcove rejects externally-hosted plugin files, so copy the build onto
    // the customer's own account first and reference the URLs it hands back.
    // Filenames are deterministic per version, so re-running this is a no-op
    // on Brightcove's side.
    const hosted = await uploadPluginAssets({
      account_id: account.account_id,
      ver: chosen.ver,
      js: chosen.js,
      css: chosen.css,
    });

    const scripts = [];
    const stylesheets = [];
    hosted.js && scripts.push(hosted.js);
    hosted.css && stylesheets.push(hosted.css);

    if (scripts.length === 0 && stylesheets.length === 0) {
      throw new Error(
        `Brightcove returned no hosted URLs for plugin ${chosen.ver}`
      );
    }

    // Deep copy: a shallow spread leaves config.plugins pointing into the
    // loaded `players` state, so a failed update would still have mutated the
    // table in place and look as though it had worked.
    const config = JSON.parse(
      JSON.stringify(player.branches.master.configuration)
    );

    // The player template version is deliberately left alone — it is the
    // client's setting, and the point of this flow is to swap the Equal Play
    // plugin while leaving everything else exactly as the client has it.

    const plugin = config.plugins.find((p) => p.name === "equalplay");
    if (!plugin) {
      throw new Error("Cant find plugin");
    }
    plugin.scripts = scripts;
    plugin.stylesheets = stylesheets;

    return await updateBCPlayer(account, id, config);
  };

  const selectedPlayer = filterPlayer(updateModal);
  const selectedPlugin = selectedPlayer
    ? selectedPlayer.branches.master.configuration.plugins?.find(
        (p) => p.name === "equalplay"
      )
    : null;

  // Plugin builds for the dropdown. getPlugins() returns them ordered by date
  // desc, so the first entry is the newest and makes a sensible default.
  useEffect(() => {
    getPlugins()
      .then((list) => {
        setPluginVersions(list);
        if (list.length > 0) setSelectedPluginVer(list[0].ver);
      })
      .catch((err) => console.error("Could not load plugin versions", err));
  }, []);

  useEffect(() => {
    if (account) return;
    getBcAccount(bcid)
      .then((acc) => {
        setAccount(acc);
        setName(acc?.name);
        setPlayerId(acc?.player_id);
        setSignLanguageProfile(acc?.signLanguageProfile ?? "");
        setSignLanguageLabel(acc?.signLanguageLabel ?? "");
        getPlayers(acc);
      })
      .catch((err) => {
        console.error(err);
      });
    // eslint-disable-next-line
  }, [bcid, account]);

  useEffect(() => {
    if (!account?.account_id) return;
    getBcIngestProfiles(account)
      .then((res) => {
        if (res.unauthorized) {
          setIngestProfilesUnauthorized(true);
          setIngestProfiles([]);
        } else {
          setIngestProfilesUnauthorized(false);
          setIngestProfiles(
            Array.isArray(res.profiles) ? res.profiles : []
          );
        }
      })
      .catch((err) => {
        console.error("Error fetching ingest profiles", err);
        setIngestProfiles([]);
      });
    // eslint-disable-next-line
  }, [account?.account_id]);

  return (
    <>
      <div className="w-56">
        <h2 className="text-xl font-semibold">{name} ({bcid})</h2>
        {!account && <Spinner />}
        {account && (
          <>
            <Input
              css="mt-2"
              label={`Account name`}
              placeholder="Name the account"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              disabled={false}
            />
            <Input
              css="mt-2"
              label={`Preview Player ID`}
              placeholder="ID of Brightcove player"
              value={playerId}
              onChange={(e) => {
                setPlayerId(e.target.value);
              }}
              disabled={false}
            />

            <div className="mt-2">
              <label className="block text-sm text-gray-700">
                SL Dynamic Ingest Profile
              </label>
              {ingestProfiles === undefined ? (
                <div className="mt-1">
                  <Spinner />
                </div>
              ) : ingestProfilesUnauthorized ? (
                <Input
                  css="mt-1"
                  placeholder="Profile ID (API key can't list profiles — enter manually)"
                  value={signLanguageProfile}
                  onChange={(e) => {
                    setSignLanguageProfile(e.target.value);
                  }}
                  disabled={false}
                />
              ) : ingestProfiles.length === 0 ? (
                <div className="mt-1 text-sm text-gray-500 italic">
                  No profiles found for this account
                </div>
              ) : (
                <select
                  className="mt-1 w-full h-10 pl-2 rounded border border-gray-200 bg-white"
                  value={signLanguageProfile}
                  onChange={(e) => setSignLanguageProfile(e.target.value)}
                >
                  <option value="">— Select a profile —</option>
                  {ingestProfiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <Input
              css="mt-2"
              label={`Default sign language label`}
              placeholder="SL"
              value={signLanguageLabel}
              onChange={(e) => {
                setSignLanguageLabel(e.target.value);
              }}
              disabled={false}
            />
            <p className="mt-1 text-xs text-gray-600">
              Fills in the label on each new sign language track, for example
              BSL or ISL. Operators can still change it per track. Left empty,
              new tracks get "SL".
            </p>

            <Button
              text={t("Save")}
              colour="white"
              onClick={() => {
                handleSave();
              }}
              disabled={
                name === account.name &&
                playerId === account.player_id &&
                signLanguageProfile === (account.signLanguageProfile ?? "") &&
                signLanguageLabel.trim() === (account.signLanguageLabel ?? "")
              }
            />
            {/* <Button
              text={"Tests"}
              colour="white"
              onClick={() => {
                updatePlayer("27");
              }}
              disabled={false}
            /> */}
          </>
        )}
      </div>
      <div>
        <h2 className="text-xl font-semibold">Players with plugin:</h2>
        {!players && <Spinner />}
        <div className="mt-2 bg-white rounded border border-gray-200 text-sm">
          <div className="px-3 py-2 grid grid-cols-12 text-xs text-gray-600 uppercase font-semibold leading-relaxed">
            <div className="col-span-3">{t("Name")}</div>
            <div className="col-span-3">{"Player ID"}</div>
            <div className="col-span-1 mx-auto">{"Update"}</div>
            <div className="col-span-5 mx-auto">{"Version"}</div>
          </div>
          {players && (
            <>
              {getPlayersWithPlugin().map((player) => {
                // console.log(player);
                return (
                  <div
                    className="px-3 py-4 grid grid-cols-12 border-t border-gray-200"
                    key={player.id}
                  >
                    <div className="col-span-3 font-semibold">
                      {player.name}
                    </div>
                    <div className="col-span-3">{player.id}</div>
                    <div className="col-span-1 mx-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setUpdateModal(player.id);
                        }}
                      >
                        <LinkIcon />
                      </button>
                    </div>
                    <div className="col-span-5 mx-auto">
                      <p>{player.plugin.scripts.toString()}</p>
                      <p>{player.plugin.stylesheets.toString()}</p>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
      <Modal
        width={"w-96"}
        show={updateModal ? true : false}
        title={"Update player"}
        subtitle={`"${selectedPlayer ? selectedPlayer.name : "unknown"}"`}
        onClose={() => {
          setUpdateModal(null);
        }}
        acceptButtonText={"Update"}
        acceptDisabled={!selectedPluginVer || updating}
        onAccept={() => {
          const version = selectedPluginVer;
          setUpdating(true);
          updatePlayer(updateModal)
            .then(() => {
              setUpdating(false);
              setUpdateModal(null);
              setAccount(null);
              addNotification(
                "Player updated",
                `Equal Play plugin set to ${version}.`,
                undefined
              );
            })
            .catch((err) => {
              setUpdating(false);
              console.error("Error updating player:", err);
              addNotification(
                "Player update failed",
                err?.message || "Please try again.",
                "bg-red-600"
              );
            });
        }}
        working={updating}
      >
        {selectedPlayer &&
          (() => {
            const config = selectedPlayer.branches.master.configuration;
            const current = installedPluginVersion(selectedPlugin);
            return (
              <>
                <h3 className="font-semibold">Template</h3>
                <p>{config.player.template.name}</p>
                <p>{config.player.template.version}</p>
                <p className="mt-1 text-xs text-gray-600">
                  Left as-is — only the Equal Play plugin is changed.
                </p>

                <h3 className="mt-4 font-semibold">Equal Play plugin</h3>
                <p className="text-sm">
                  Installed: <strong>{current || "unknown"}</strong>
                </p>

                {hasExternalAssets(selectedPlugin) && (
                  <p className="mt-2 text-sm text-gray-700 bg-yellow-100 border border-yellow-300 rounded px-3 py-2">
                    This player loads the plugin from outside Brightcove, which
                    is what causes “Inactive — Outdated Asset URL(s)”. Updating
                    uploads the files to this account and fixes it.
                  </p>
                )}

                <label className="mt-2 block text-sm" htmlFor="pluginVersion">
                  Version to install
                </label>
                <select
                  id="pluginVersion"
                  name="pluginVersion"
                  className="mt-1 w-full px-2 py-2 border border-gray-200 rounded focus:outline-none focus:shadow-outline"
                  value={selectedPluginVer}
                  onChange={(e) => setSelectedPluginVer(e.target.value)}
                >
                  {!pluginVersions && <option value="">Loading…</option>}
                  {pluginVersions && pluginVersions.length === 0 && (
                    <option value="">No versions in the catalogue</option>
                  )}
                  {pluginVersions &&
                    pluginVersions.map((p, index) => (
                      <option key={p.ver} value={p.ver}>
                        {p.ver}
                        {index === 0 ? " (latest)" : ""}
                        {p.ver === current ? " — installed" : ""}
                      </option>
                    ))}
                </select>

                {current && selectedPluginVer && selectedPluginVer === current && (
                  <p className="mt-2 text-xs text-gray-600">
                    This is the version already installed — updating will
                    re-apply the same files.
                  </p>
                )}
              </>
            );
          })()}
      </Modal>
    </>
  );
}

export default BcAccount;
